/**
 * specModes.js — движок под вкладкой «Спец. режимы»: откуда брать значения
 * снимка и в какое окно смотрит врач. [12.0.57]
 *
 * ПОВОД (стенд tests/стенды/specrezhimy, настоящий Cornerstone 5.6.3):
 *   • «Умный W/L» и «ROI W/L» брали значения через getVolumeScalarData — в
 *     окнах MPR она пуста, и тост печатал «медиана NaN HU», «WW=NaN / WL=NaN»;
 *     точку щелчка переводили в воксель по прямоугольнику ВСЕЙ области окон —
 *     в раскладке из пяти окон это было чужое место чужого окна;
 *   • лупа, сетка, слэб, закладки, камера, авто-W/L брали «первое окно
 *     раскладки» (getViewportIds()[0]) — в MPR 6 это стопка «Нативные срезы»,
 *     и слэб MIP «включался» на стопке, где его не бывает, с тостом «MIP 10mm».
 *
 * Здесь один ответ на оба вопроса:
 *   • какое окно — выбранное щелчком (viewportState.текущееОкноСнимка, им же
 *     пользуется окно W/L с 12.0.49), а для щелчка по снимку — то, по
 *     которому щёлкнули;
 *   • откуда значения — стопка: её текущий снимок; реформат: его том из
 *     кэша Cornerstone; точка щелчка — canvasToWorld самого окна. Значения
 *     читаются по вокселю (voxelManager.getAtIndex), без копии тома.
 *
 * Математика — core/specModesCore.js (исполняется тестом на числах);
 * откуда брать числа окна — core/viewportValues.js (им пользуется и лупа).
 *
 * EXPORTS: окноВыбранное, данныеОкна, плоскостьОкна, окноПодСобытием,
 *   пробаПодМышью, срезОкна, аннотацииСреза, номерСреза, модальностьОкна
 */
import { getRenderingEngine, текущееОкноСнимка, ViewportType } from '../core/viewportState.js';
import { данныеОкна, плоскостьОкна, номерСреза, модальностьОкна } from '../core/viewportValues.js';
import { пробаВПлоскости, осиВида, срезВида, фигураВПлоскости, вФигуре, наПлоскости } from '../core/specModesCore.js';
import { getAnnotations } from '../tools/toolManager.js';

/**
 * Окно снимка, выбранное врачом. Если просят плоское окно, а выбрано
 * объёмное 3D — отказ словами (а не тихий переход в первое попавшееся).
 * @param {{плоское?:boolean}} [о]
 * @returns {{ok:true, id:string, vp:object}|{ok:false, причина:string}}
 */
export function окноВыбранное(о = {}) {
  const re = getRenderingEngine();
  const id = текущееОкноСнимка();
  const vp = id && re?.getViewport(id);
  if (!vp) return { ok: false, причина: 'нет открытого окна со снимком — откройте исследование' };
  if (о.плоское && vp.type === ViewportType.VOLUME_3D) {
    return { ok: false, причина: 'выбрано объёмное 3D-окно — щёлкните по окну со срезом (аксиальное, боковое, фронтальное или стопка)' };
  }
  return { ok: true, id, vp };
}

/** Окно под событием мыши (элемент viewport-<id>) — или null. */
export function окноПодСобытием(e) {
  const эл = e?.target?.closest?.('[id^="viewport-"]');
  if (!эл) return null;
  const id = эл.id.slice('viewport-'.length);
  const vp = getRenderingEngine()?.getViewport(id);
  return vp ? { id, vp, эл } : null;
}

/**
 * Проба вокруг точки щелчка в плоскости окна, по которому щёлкнули.
 * @param {MouseEvent} e
 * @param {number} полуN — 3 даёт 7×7 вокселей, 10 — 21×21
 */
export function пробаПодМышью(e, полуN = 3) {
  const о = окноПодСобытием(e);
  if (!о) return { ok: false, причина: 'щелчок не по окну снимка' };
  const д = данныеОкна(о.vp);
  if (!д.ok) return д;
  const п = о.vp.element.getBoundingClientRect();
  const мир = о.vp.canvasToWorld([e.clientX - п.left, e.clientY - п.top]);
  const пл = плоскостьОкна(о.vp);
  const значения = пробаВПлоскости(д.данные, мир, осиВида(пл.нормаль, пл.верх), полуN);
  if (!значения.length) return { ok: false, причина: 'щелчок вне снимка' };
  return { ok: true, id: о.id, vp: о.vp, мир, значения, модальность: д.модальность };
}

const ФИГУРЫ = /^(RectangleROI|EllipticalROI|CircleROI)$/;

/** ROI-аннотации (прямоугольник, эллипс, круг), лежащие в плоскости окна. */
export function аннотацииСреза(vp, данные) {
  const пл = плоскостьОкна(vp);
  const допуск = Math.max(0.5, Math.min(...данные.spacing) / 2);
  let все = [];
  try { все = getAnnotations() || []; } catch (e) { все = []; }
  if (!Array.isArray(все)) все = [];
  return все.filter((а) => ФИГУРЫ.test(а?.metadata?.toolName || '')
    && наПлоскости(а?.data?.handles?.points || [], пл, допуск));
}

/**
 * Срез, который сейчас на экране окна, — целиком или внутри ROI, если на
 * этом срезе нарисована область (берётся последняя нарисованная).
 * @returns {{ok:true, значения:Float32Array|number[], где:string, номер?:number, всего?:number, модальность:string}|{ok:false, причина:string}}
 */
export function срезОкна(vp) {
  const д = данныеОкна(vp);
  if (!д.ok) return д;
  const пл = плоскостьОкна(vp);
  const с = срезВида(д.данные, пл);
  if (!с) return { ok: false, причина: 'плоскость окна вне объёма' };
  const н = номерСреза(vp);
  const roi = аннотацииСреза(vp, д.данные);
  if (roi.length) {
    const а = roi[roi.length - 1];
    const ф = фигураВПлоскости(а.metadata.toolName, а.data.handles.points, пл);
    if (ф) {
      const внутри = [];
      for (let b = 0; b < с.высота; b++) for (let a = 0; a < с.ширина; a++) {
        const v = с.значения[b * с.ширина + a];
        if (Number.isFinite(v) && вФигуре(ф, с.мир(a, b), пл)) внутри.push(v);
      }
      if (внутри.length) {
        return { ok: true, значения: внутри, где: `внутри области «${{ RectangleROI: 'прямоугольник', EllipticalROI: 'эллипс', CircleROI: 'круг' }[а.metadata.toolName]}»`, ...н, модальность: д.модальность };
      }
    }
  }
  const значения = с.ровно ? с.значения : с.значения.filter((v) => Number.isFinite(v));
  return { ok: true, значения, где: 'весь срез', ...н, модальность: д.модальность };
}

export { данныеОкна, плоскостьОкна, номерСреза, модальностьОкна };
export default { окноВыбранное, данныеОкна, плоскостьОкна, окноПодСобытием, пробаПодМышью, срезОкна, аннотацииСреза, номерСреза, модальностьОкна };
